// Compact icon-only theme switcher for the in-app chrome strips.
// Shares the theme list with ThemeSwitcher but presents as a Palette
// button with a popout dropdown. Used where a text label would compete
// with the UserPopout + Brand for space.

import { useEffect, useMemo, useRef, useState } from "react";
import { Palette } from "lucide-react";
import { THEME_NAMES, THEMES, type ThemeName } from "./themes";
import { useTheme } from "./ThemeContext";

function luminance(hex: string): number {
  const h = hex.replace("#", "");
  if (h.length !== 6) return 0.5;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return 0.5;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export function ThemeIcon() {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const { darkThemes, lightThemes } = useMemo(() => {
    const darks: ThemeName[] = [];
    const lights: ThemeName[] = [];
    for (const name of THEME_NAMES) {
      if (luminance(THEMES[name].bg) < 0.5) darks.push(name);
      else lights.push(name);
    }
    return { darkThemes: darks, lightThemes: lights };
  }, []);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function renderSection(label: string, names: ThemeName[]) {
    if (names.length === 0) return null;
    return (
      <div className="flex flex-col">
        <span className="px-2 pt-1 pb-0.5 text-[0.625rem] font-medium text-faint">
          {label}
        </span>
        {names.map((name) => (
          <button
            key={name}
            onClick={() => {
              setTheme(name);
              setOpen(false);
            }}
            aria-checked={theme === name}
            role="menuitemradio"
            className={`block w-full cursor-pointer rounded px-2 py-1 text-left text-xs font-medium transition-colors ${
              theme === name
                ? "bg-accent text-accent-text"
                : "text-muted hover:bg-surface hover:text-primary"
            }`}
          >
            {THEMES[name].label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Theme"
        title={`Theme: ${THEMES[theme].label}`}
        className={`relative cursor-pointer transition-colors ${
          open ? "text-primary" : "text-muted hover:text-primary"
        }`}
        data-testid="theme-icon-toggle"
      >
        <Palette size={14} className="block" />
        {open && (
          <span
            aria-hidden
            className="absolute -bottom-2 left-0 right-0 h-px bg-accent"
          />
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-3 flex max-h-96 w-40 flex-col gap-2 overflow-y-auto rounded-md border border-edge bg-surface-raised p-2 shadow-[var(--wt-shadow)]"
          data-testid="theme-icon-menu"
          role="menu"
        >
          {renderSection("Dark", darkThemes)}
          {renderSection("Light", lightThemes)}
        </div>
      )}
    </div>
  );
}
